import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Style from './Governance.module.css';
import GridComponent from '../common/grid/GridComponent';
import SearchBox from '../common/searchBox/searchBox';
import { editorPath, DISPOSITION_SANCTIONED, DISPOSITION_DENIED } from './GovernanceShared';
import { listUnsanctionedApps, setUnsancAppsFilters } from '../../store/entities/governance';

// Solution doc §9.2: the read endpoint supports sortBy users|firstSeen|lastSeen|domain
// x sortDir asc|desc, but the UI surface for it is a single sort dropdown (mock-up:
// claude-dashboards/dashboards/governance.html, Unsanctioned Apps toolbar), not
// per-column header sorting like PolicyViolations.jsx uses -- these 4 options are the
// exact combinations the mock-up wires up. Order matches the mock-up's <select>.
const SORT_OPTIONS = [
  { value: 'users-desc', sortBy: 'users', sortDir: 'desc', label: 'Sort: Most users' },
  { value: 'users-asc', sortBy: 'users', sortDir: 'asc', label: 'Sort: Fewest users' },
  { value: 'lastSeen-desc', sortBy: 'lastSeen', sortDir: 'desc', label: 'Sort: Most recent' },
  { value: 'domain-asc', sortBy: 'domain', sortDir: 'asc', label: 'Sort: A–Z' },
];

// ─────────────────────────────────────────────────────────────────────────────
// UnsanctionedApps — PIXMC-6083 §9/§10. Rendered below AppControls on the WEB tab
// only (Web-only per §10.1/D13). Lists domains the daily discovery job found in
// extension heartbeats but no admin has reviewed yet — server-driven search/sort/
// pagination, same GridComponent + SearchBox pattern as PolicyViolations.jsx.
//
// Every discovered domain already has a policy (created UNSANCTIONED by the job), so
// Sanction/Deny deep-link into the existing site editor via policyId (D4) instead of
// calling addSite. The editor still requires Save Draft -> Submit for Approval ->
// Publish before the disposition actually changes, so these buttons only START that
// flow — they intentionally do not imply the click alone sanctions/denies the site.
// ─────────────────────────────────────────────────────────────────────────────
const UnsanctionedApps = (props) => {
  const { customerId, selectedCustomer, history, records, totalCount, lookbackDays, loading, filters } = props;

  const handleSearch = (searchText) => props.setUnsancAppsFilters({ search: searchText, page: 1 });

  const handleSortChange = (e) => {
    const opt = SORT_OPTIONS.find((o) => o.value === e.target.value);
    if (opt) props.setUnsancAppsFilters({ sortBy: opt.sortBy, sortDir: opt.sortDir, page: 1 });
  };

  // presetDisposition: which button was clicked, so the editor opens with that
  // disposition already selected instead of the site's actual (UNSANCTIONED)
  // server state — see GovernanceShared.editorPath's comment for the full why.
  const openInEditor = (policyId, presetDisposition) => {
    if (customerId && policyId) history.push(editorPath(customerId, 'WEB', policyId, presetDisposition));
  };

  const gridDef = {
    name: 'unsanctionedAppsGrid',
    columns: [
      { fieldName: 'domain', displayName: 'Site / App', width: '28%', className: Style.domCell },
      { fieldName: 'users', displayName: 'Users', width: '16%' },
      { fieldName: 'firstSeen', displayName: 'First Seen', dataType: 'date', subType: 'datetime', width: '26%' },
      // noTooltip: this cell's content is the Sanction/Deny buttons themselves (JSX,
      // not a string) -- GridComponent's default per-cell tooltip would duplicate that
      // JSX into a second, non-interactive floating copy on hover. The buttons already
      // carry their own native `title` tooltip, so the grid-level one is only ever
      // redundant here, never additive.
      { fieldName: 'action', displayName: 'Action', width: '30%', noTooltip: true },
    ],
  };

  const gridRecords = records.map((r) => ({
    ...r,
    action: (
      <div className={Style.rowActions}>
        <button
          className={Style.btn}
          title="Opens the site editor with Sanctioned pre-selected (requires Save Draft, Approval, and Publish)"
          onClick={() => openInEditor(r.policyId, DISPOSITION_SANCTIONED)}
        >
          Sanction
        </button>
        <button
          className={Style.btn}
          title="Opens the site editor with Denied pre-selected (requires Save Draft, Approval, and Publish)"
          onClick={() => openInEditor(r.policyId, DISPOSITION_DENIED)}
        >
          Deny
        </button>
      </div>
    ),
  }));

  const getNextPageData = (pageNo, pageSize) => props.listUnsanctionedApps(customerId, { ...filters, page: pageNo, pageSize });
  const getPrevPageData = (pageNo, pageSize) => props.listUnsanctionedApps(customerId, { ...filters, page: pageNo, pageSize });

  // selectedCustomer?.id is REQUIRED here (mirrors PolicyViolations.jsx). On a hard refresh
  // selectedCustomer (Redux) loads AFTER mount, so GridComponent's mount-fetch guard
  // (selectedCustomer.id === params) is false and the fetch is skipped; nothing else retries it
  // unless this key changes. Route customerId + filters are all stable across a refresh, so without
  // selectedCustomer?.id the grid would stay empty on refresh. Its arrival flips the key and refetches.
  const resetOnChange = [customerId, selectedCustomer?.id, filters.search, filters.sortBy, filters.sortDir].join('|');

  const from = totalCount === 0 ? 0 : (filters.page - 1) * filters.pageSize + 1;
  const to = Math.min(filters.page * filters.pageSize, totalCount);
  const sortValue = `${filters.sortBy}-${filters.sortDir}`;

  return (
    <div className={Style.card}>
      <div className={Style.cardHeader}>
        <div className={Style.cardTitle}>Unsanctioned Apps</div>
        <div className={Style.cardSubtitle}>
          Detected, not yet approved — sites and apps seen in extension heartbeats over the last{' '}
          {lookbackDays || 30} days that no admin has sanctioned or denied. A domain drops off this list once
          it is published with a disposition.
        </div>
      </div>
      <div className={Style.toolbar}>
        <SearchBox width="280px" placeholder="Search by domain..." value={filters.search} handleSearch={handleSearch} />
        <select className={Style.sortSelect} value={sortValue} onChange={handleSortChange}>
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <div className={Style.toolbarSpacer} />
        <div className={Style.resultCount}>
          {totalCount > 0 ? `Showing ${from}–${to} of ${totalCount}` : 'No unsanctioned apps detected'}
        </div>
      </div>
      <div className={Style.cardBody}>
        <GridComponent
          loaderFlag={loading}
          resetOnChange={resetOnChange}
          data={gridRecords}
          totalRecords={totalCount}
          pageSize={filters.pageSize}
          renderFullPage
          definition={gridDef}
          getPrevPageData={getPrevPageData}
          getNextPageData={getNextPageData}
          rowClick={() => {}}
          params={customerId}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  selectedCustomer: state.entities.customers.selected,
  records: state.entities.governance.unsancApps,
  totalCount: state.entities.governance.unsancAppsTotalCount,
  lookbackDays: state.entities.governance.unsancAppsLookbackDays,
  loading: state.entities.governance.unsancAppsLoading,
  filters: state.entities.governance.unsancAppsFilters,
});

const mapDispatchToProps = (dispatch) => ({
  listUnsanctionedApps: (customerId, filters) => dispatch(listUnsanctionedApps(customerId, filters)),
  setUnsancAppsFilters: (partial) => dispatch(setUnsancAppsFilters(partial)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(UnsanctionedApps));
